import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { SurfacePage, SoftSurface } from '../components/surface';
import LoadingSpinner from '../components/LoadingSpinner';
import ProPageEditor from '../components/pro/setup/ProPageEditor';
import { STEPS, SKIPS as SKIP_KEYS, visibleFor } from '../components/pro/setup/proSetupSteps';
import { proService } from '../services';
import useMediaQuery from '../utils/useMediaQuery';

/**
 * /pro/setup — the first-time stepper (design_handoff_pro_setup README §1). It only runs while a
 * page is new or not yet published: a published page landing here goes to the dashboard's EDIT
 * PAGE tab, where every section lives from then on. `?new=1` starts another page for an account
 * that already has one; `?step=N` (the global 1–12 number, kept for old links) deep-links a step.
 */
export const STEP_KEYS = STEPS;
export const SKIPS = SKIP_KEYS;
export const stepsFor = (kind) => visibleFor(kind).map((key) => ({ n: STEPS.indexOf(key) + 1, key }));

export default function ProSetupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const phone = useMediaQuery('(max-width: 639px)');
  const creatingNew = params.get('new') === '1';
  // Set when they arrive from a finished claim: the black banner across the top.
  const claimed = location.state?.claimed ? { waiting: location.state.waitingRequests || 0 } : null;
  const [gate, setGate] = useState(creatingNew ? 'open' : 'checking');
  const [preloaded, setPreloaded] = useState(null);
  // Decided once on arrival: `?new` leaves the URL after the first save and must not re-run the check.
  const [startedNew] = useState(creatingNew);

  // A published page has no stepper any more — its sections are the EDIT PAGE tab.
  useEffect(() => {
    if (startedNew) return undefined;
    let cancelled = false;
    proService.getMine().then((r) => {
      if (cancelled) return;
      if (r?.success && r.data?.isPublished) navigate('/pro/dashboard?tab=edit', { replace: true });
      else { setPreloaded({ page: r?.success ? r.data : null }); setGate('open'); }
    });
    return () => { cancelled = true; };
  }, [startedNew, navigate]);

  const requested = parseInt(params.get('step') || '', 10);
  const initialStep = requested >= 1 && requested <= STEPS.length ? STEPS[requested - 1] : null;
  const onStepChange = (key) => {
    const next = new URLSearchParams(params);
    next.set('step', String(STEPS.indexOf(key) + 1));
    // The first save of a new page wears it as the persona; `?new` leaves so a reload doesn't start a third.
    next.delete('new');
    setParams(next, { replace: true });
  };

  return (
    <SoftSurface>
      <SurfacePage bare showTopBarOnMobile>
        <main className="mx-auto flex w-full flex-col font-mono" style={{ maxWidth: 1180, padding: phone ? '12px 12px 24px' : '24px 32px 72px', gap: phone ? 10 : 16, boxSizing: 'border-box' }} data-testid="pro-setup">
          {gate === 'checking'
            ? <div className="py-24 text-center"><LoadingSpinner label="LOADING…" /></div>
            : <ProPageEditor mode="create" creatingNew={startedNew} preloaded={preloaded} claimed={claimed} initialStep={initialStep} onStepChange={onStepChange} />}
        </main>
      </SurfacePage>
    </SoftSurface>
  );
}
